// components/CollaborationPanel.jsx — Sharing, Roles, Comments & Review
import { useState, useEffect, useRef } from 'react';
import { supabase } from '../utils/supabaseClient';
import { contractsDb } from '../utils/store';

const ROLE_CONFIG = {
  editor:   { label: 'Editor',   color: 'var(--blue-600)',   bg: 'var(--blue-50)',   desc: 'Full edit access — can edit fields, advance stages, use Penny' },
  reviewer: { label: 'Reviewer', color: 'var(--purple-600)', bg: 'var(--purple-50)', desc: 'Can leave comments and annotations — cannot edit fields' },
  approver: { label: 'Approver', color: 'var(--gold-600)',   bg: 'var(--gold-50)',   desc: 'Can comment and mark Approved / Needs Revision' },
  viewer:   { label: 'Viewer',   color: 'var(--slate-500)',  bg: 'var(--slate-50)',  desc: 'Read-only — can view all tabs, no comments' },
};

const SECTION_LABELS = {
  stage: 'Stage & Info', penny: 'Penny Tab', std213: 'STD 213',
  std215: 'STD 215', assembly: 'Assembly', general: 'General',
};

function Avatar({ name, size = 32 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--navy-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: size * 0.4, fontWeight: 700, flexShrink: 0 }}>
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}


function CommentThread({ comment, replies, currentUserId, isOwner, canComment, onReply, onResolve, onDelete, profiles }) {
  const [replyText, setReplyText] = useState('');
  const [showReply, setShowReply] = useState(false);
  const authorProfile = profiles[comment.user_id];
  const authorName = authorProfile?.contact_name || 'Unknown';
  const timeAgo = (ts) => {
    const mins = Math.floor((Date.now() - new Date(ts)) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return mins + 'm ago';
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + 'h ago';
    return Math.floor(hrs / 24) + 'd ago';
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ padding: '12px 14px', borderRadius: 'var(--r-md)', border: '1px solid ' + (comment.resolved ? 'var(--slate-100)' : 'var(--slate-200)'), background: comment.resolved ? 'var(--slate-50)' : '#fff', opacity: comment.resolved ? 0.7 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <Avatar name={authorName} size={28} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-800)' }}>{authorName}</span>
              {comment.section && (
                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 99, background: 'var(--navy-100)', color: 'var(--navy-600)', fontWeight: 600 }}>
                  {SECTION_LABELS[comment.section] || comment.section}
                  {comment.field ? ' · ' + comment.field : ''}
                </span>
              )}
              <span style={{ fontSize: 11, color: 'var(--slate-400)', marginLeft: 'auto' }}>{timeAgo(comment.created_at)}</span>
              {comment.resolved && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--green-600)' }}>✓ Resolved</span>}
            </div>
            <div style={{ fontSize: 13, color: 'var(--slate-700)', marginTop: 6, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{comment.comment}</div>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              {canComment && !comment.resolved && (
                <button onClick={() => setShowReply(p => !p)} style={{ fontSize: 11, color: 'var(--navy-600)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  ↩ Reply
                </button>
              )}
              {(isOwner || comment.user_id === currentUserId) && !comment.resolved && (
                <button onClick={() => onResolve(comment.id)} style={{ fontSize: 11, color: 'var(--green-600)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  ✓ Resolve
                </button>
              )}
              {comment.user_id === currentUserId && (
                <button onClick={() => onDelete(comment.id)} style={{ fontSize: 11, color: 'var(--red-400)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Replies */}
      {replies.length > 0 && (
        <div style={{ marginLeft: 24, borderLeft: '2px solid var(--slate-100)', paddingLeft: 12, marginTop: 6 }}>
          {replies.map(r => {
            const rAuthor = profiles[r.user_id]?.contact_name || 'Unknown';
            return (
              <div key={r.id} style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', background: 'var(--slate-50)', marginBottom: 6, border: '1px solid var(--slate-100)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar name={rAuthor} size={22} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-700)' }}>{rAuthor}</span>
                  <span style={{ fontSize: 10, color: 'var(--slate-400)', marginLeft: 'auto' }}>{timeAgo(r.created_at)}</span>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--slate-700)', marginTop: 4, lineHeight: 1.6 }}>{r.comment}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reply input */}
      {showReply && canComment && (
        <div style={{ marginLeft: 24, marginTop: 6, display: 'flex', gap: 8 }}>
          <input value={replyText} onChange={e => setReplyText(e.target.value)}
            placeholder="Write a reply…"
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (replyText.trim()) { onReply(comment.id, replyText.trim()); setReplyText(''); setShowReply(false); } } }}
            style={{ flex: 1, padding: '6px 10px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12.5, fontFamily: 'inherit' }} />
          <button className="btn btn-gold btn-sm" disabled={!replyText.trim()}
            onClick={() => { if (replyText.trim()) { onReply(comment.id, replyText.trim()); setReplyText(''); setShowReply(false); } }}>
            Reply
          </button>
        </div>
      )}
    </div>
  );
}

export default function CollaborationPanel({ contract, currentUserId, toast, apiKey, callAI, updateC, onAdvanceStage }) {
  const [members, setMembers] = useState([]);
  const [comments, setComments] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('reviewer');
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [newSection, setNewSection] = useState('general');
  const [newField, setNewField] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [filterSection, setFilterSection] = useState('all');
  const [showResolved, setShowResolved] = useState(false);
  const [activeTab, setActiveTab] = useState('comments');

  const [pennyLoading, setPennyLoading] = useState('');
  const [reviewPackages, setReviewPackages] = useState([]);
  const [submittingPackage, setSubmittingPackage] = useState(false);
  const [packageNote, setPackageNote] = useState('');
  const [reviewStage, setReviewStage] = useState('legal');
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [showSubmitForm, setShowSubmitForm] = useState(null); // holds stage name: 'legal' | 'supervisor' | null
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [pendingDecision, setPendingDecision] = useState(null); // 'approved' | 'needs_revision'
  const [decisionNotes, setDecisionNotes] = useState('');
  const [isBypassing, setIsBypassing] = useState(false);
  const [bypassReason, setBypassReason] = useState('');
  const [pennySummary, setPennySummary] = useState('');
  const [selectedComment, setSelectedComment] = useState(null);
  const [pennyResolution, setPennyResolution] = useState('');

  const isOwner = contract?.ownerId === currentUserId;
  const myMember = members.find(m => m.user_id === currentUserId);
  const myRole = isOwner ? 'editor' : myMember?.role;
  const canComment = myRole && myRole !== 'viewer';
  const canApprove = isOwner || myRole === 'approver';

  useEffect(() => {
    if (contract?.id) { loadMembers(); loadComments(); loadReviewPackages(); }
  }, [contract?.id]); // eslint-disable-line

  async function loadMembers() {
    setLoading(true);
    try {
      const data = await contractsDb.getMembers(contract.id);
      setMembers(data || []);
      // Load profiles for all members + owner
      const ids = [...new Set([contract.ownerId, ...(data || []).map(m => m.user_id)])].filter(Boolean);
      if (ids.length) {
        const { data: profs } = await supabase.from('user_profiles').select('id, contact_name, contact_email').in('id', ids);
        const profMap = {};
        (profs || []).forEach(p => { profMap[p.id] = p; });
        setProfiles(profMap);
      }
    } catch (e) { console.error('loadMembers error:', e); }
    finally { setLoading(false); }
  }

  async function loadComments() {
    const { data, error } = await supabase
      .from('contract_comments')
      .select('*')
      .eq('contract_id', contract.id)
      .order('created_at', { ascending: true });
    if (!error && data) {
      setComments(data);
      // Load profiles for commenters
      const ids = [...new Set(data.map(c => c.user_id))];
      if (ids.length) {
        const { data: profs } = await supabase.from('user_profiles').select('id, contact_name, contact_email').in('id', ids);
        setProfiles(prev => {
          const m = { ...prev };
          (profs || []).forEach(p => { m[p.id] = p; });
          return m;
        });
      }
    }
  }

  async function loadReviewPackages() {
    const { data } = await supabase.from('contract_review_packages')
      .select('*').eq('contract_id', contract.id).order('submitted_at', { ascending: false });
    if (data) setReviewPackages(data);
  }

  async function submitReviewPackage() {
    if (selectedDocs.length === 0) { toast.warn('Select at least one document to include'); return; }
    setSubmittingPackage(true);
    try {
      const openCount = rootComments.filter(c => !c.resolved).length;
      const version = reviewPackages.length + 1;
      const { data: pkg, error } = await supabase.from('contract_review_packages').insert({
        contract_id: contract.id,
        submitted_by: currentUserId,
        version,
        review_stage: reviewStage,
        note: packageNote.trim() || null,
        documents: selectedDocs,
        status: 'pending',
        open_comments: openCount,
        stage_at_submit: contract.stage,
      }).select().single();
      if (error) throw error;

      // Update contract review status to pending
      await supabase.from('contracts').update({ review_status: 'pending', reviewed_by: null, reviewed_at: null }).eq('id', contract.id);
      if (updateC) {
        const includedIds = selectedDocs.map(d => d.id);
        const statusUpdates = { reviewStatus: 'pending' };
        if (includedIds.includes('exhibitA') || includedIds.includes('fullPackage')) statusUpdates.exAStatus = 'in_review';
        if (includedIds.includes('exhibitB') || includedIds.includes('fullPackage')) statusUpdates.exBStatus = 'in_review';
        if (includedIds.includes('exhibitD') || includedIds.includes('fullPackage')) statusUpdates.exDStatus = 'in_review';
        if (includedIds.includes('solicitation') || includedIds.includes('fullPackage')) statusUpdates.solStatus = 'in_review';
        updateC({ reviewStatus: 'pending', pennyDrafts: statusUpdates });
      }

      // Notify all reviewers and approvers
      const reviewerMembers = members.filter(m => ['reviewer','approver'].includes(m.role));
      const myProfile = profiles[currentUserId];
      for (const m of reviewerMembers) {
        const rProfile = profiles[m.user_id];
        if (rProfile?.contact_email) {
          await sendEmailNotification(
            rProfile.contact_email,
            rProfile.contact_name || 'Reviewer',
            'Review requested [' + REVIEW_STAGES[reviewStage]?.label + ']: "' + contract.title + '" (v' + version + ')',
            (myProfile?.contact_name || 'The analyst') + ' has submitted version ' + version + ' of "' + contract.title + '" for your review.\n\nIncluded documents:\n' + selectedDocs.map(d => '- ' + d.label).join('\n') + (packageNote ? '\n\nNote from analyst: ' + packageNote : '') + '\n\nLog in to GoldenScope to review and leave comments.'
          );
        }
      }

      setPackageNote(''); setSelectedDocs([]); setShowSubmitForm(false);
      await loadReviewPackages();
      toast.success('Review package v' + version + ' submitted — ' + reviewerMembers.length + ' reviewer(s) notified');
    } catch (e) { toast.error('Submit failed: ' + e.message); }
    finally { setSubmittingPackage(false); }
  }

  async function sendEmailNotification(toEmail, toName, subject, body) {
    try {
      await fetch('https://formspree.io/f/mbdpqdzp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          _replyto: 'noreply@goldenscope.app',
          email: toEmail,
          subject,
          message: `Hi ${toName},\n\n${body}\n\n— GoldenScope\nhttps://goldenscope.app`,
        }),
      });
    } catch (e) { console.warn('Email notification failed:', e); }
  }

  async function invite() {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data: profiles_found } = await supabase
        .from('user_profiles').select('id, contact_name, contact_email')
        .eq('contact_email', inviteEmail.trim().toLowerCase()).limit(1);

      if (!profiles_found?.length) {
        toast.error('No GoldenScope account found for ' + inviteEmail + '. They must sign up first.');
        return;
      }
      const target = profiles_found[0];
      if (target.id === currentUserId) { toast.error('You are already the owner.'); return; }
      if (members.find(m => m.user_id === target.id)) { toast.error(target.contact_name + ' already has access.'); return; }

      const { error } = await supabase.from('contract_members').insert({
        contract_id: contract.id, user_id: target.id, role: inviteRole,
        added_by: currentUserId, added_at: new Date().toISOString(),
      });
      if (error) throw error;

      const myProfile = profiles[currentUserId];
      const roleInfo = ROLE_CONFIG[inviteRole];
      await sendEmailNotification(
        target.contact_email,
        target.contact_name || 'Colleague',
        'You have been invited to review a contract on GoldenScope',
        `${myProfile?.contact_name || 'A colleague'} has shared the contract "${contract.title}" with you on GoldenScope.\n\nYour role: ${roleInfo.label}\n${roleInfo.desc}\n\nLog in to GoldenScope to view and ${inviteRole !== 'viewer' ? 'comment on' : 'review'} the contract.\n\nhttps://goldenscope.app`
      );

      toast.success(target.contact_name + ' added as ' + roleInfo.label + ' — notification sent');
      setInviteEmail('');
      loadMembers();
    } catch (e) { toast.error('Failed to add member: ' + e.message); }
    finally { setInviting(false); }
  }

  async function updateRole(userId, newRole) {
    const { error } = await supabase.from('contract_members').update({ role: newRole }).eq('contract_id', contract.id).eq('user_id', userId);
    if (error) { toast.error('Failed to update role'); return; }
    toast.success('Role updated');
    loadMembers();
  }

  async function removeMember(userId, name) {
    setRemoving(userId);
    try {
      await contractsDb.removeMember(contract.id, userId);
      toast.success((name || 'Member') + ' removed');
      loadMembers();
    } catch (e) { toast.error('Failed to remove: ' + e.message); }
    finally { setRemoving(null); }
  }

  async function submitComment() {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.from('contract_comments').insert({
        contract_id: contract.id, user_id: currentUserId,
        section: newSection, field: newField.trim() || null,
        comment: newComment.trim(),
      });
      if (error) throw error;
      setNewComment(''); setNewField('');
      await loadComments();
      // Notify owner and editors if commenter is not owner
      if (!isOwner) {
        const ownerProfile = profiles[contract.ownerId];
        if (ownerProfile?.contact_email) {
          const myProfile = profiles[currentUserId];
          await sendEmailNotification(
            ownerProfile.contact_email,
            ownerProfile.contact_name || 'Analyst',
            'New comment on "' + contract.title + '"',
            `${myProfile?.contact_name || 'A reviewer'} left a comment on "${contract.title}":\n\n"${newComment.trim()}"\n\nSection: ${SECTION_LABELS[newSection] || newSection}`
          );
        }
      }
      toast.success('Comment added');
    } catch (e) { toast.error('Failed to add comment: ' + e.message); }
    finally { setSubmitting(false); }
  }

  async function replyToComment(parentId, text) {
    const { error } = await supabase.from('contract_comments').insert({
      contract_id: contract.id, user_id: currentUserId,
      parent_id: parentId, comment: text, section: null,
    });
    if (error) { toast.error('Reply failed'); return; }
    await loadComments();
  }

  async function resolveComment(id) {
    const { error } = await supabase.from('contract_comments').update({
      resolved: true, resolved_by: currentUserId, resolved_at: new Date().toISOString(),
    }).eq('id', id);
    if (error) { toast.error('Failed to resolve'); return; }
    await loadComments();
    toast.success('Comment resolved');
  }

  async function deleteComment(id) {
    await supabase.from('contract_comments').delete().eq('id', id);
    await loadComments();
  }

  const REVIEW_STAGES = {
    legal:      { label: 'Legal Review',      color: 'var(--blue-600)',   bg: 'var(--blue-50)' },
    supervisor: { label: 'Supervisor Review', color: 'var(--purple-600)', bg: 'var(--purple-50)' },
    general:    { label: 'General Review',    color: 'var(--slate-600)',  bg: 'var(--slate-50)' },
  };

  async function confirmApprovalDecision() {
    if (isBypassing && !bypassReason.trim()) { toast.warn('A bypass reason is required'); return; }
    const status = isBypassing ? 'approved' : pendingDecision;
    setShowApprovalModal(false);
    await setApprovalStatus(status, decisionNotes.trim(), isBypassing, bypassReason.trim());
    setDecisionNotes(''); setBypassReason(''); setIsBypassing(false); setPendingDecision(null);
  }

  async function setApprovalStatus(status, notes = '', bypassed = false, bypassReason = '') {
    const { error } = await supabase.from('contracts').update({
      review_status: status, reviewed_by: currentUserId, reviewed_at: new Date().toISOString()
    }).eq('id', contract.id);
    if (!error) {
      if (updateC) updateC({ reviewStatus: status, reviewedBy: currentUserId, reviewedAt: new Date().toISOString() });
      const ownerProfile = profiles[contract.ownerId];
      const myProfile = profiles[currentUserId];
      if (ownerProfile?.contact_email && !isOwner) {
        await sendEmailNotification(
          ownerProfile.contact_email,
          ownerProfile.contact_name || 'Analyst',
          '"' + contract.title + '" has been ' + (status === 'approved' ? 'approved' : 'marked for revision'),
          `${myProfile?.contact_name || 'A reviewer'} has ${status === 'approved' ? '✅ approved' : '⚠ marked for revision'} the contract "${contract.title}".\n\nLog in to GoldenScope to review their comments.`
        );
      }
      // Update latest review package status
      if (reviewPackages.length > 0) {
        await supabase.from('contract_review_packages').update({
          status, decided_by: currentUserId, decided_at: new Date().toISOString(),
          decision_notes: notes || null,
          bypassed: bypassed || false,
          bypass_reason: bypassReason || null,
        }).eq('id', reviewPackages[0].id);
      }
      await loadReviewPackages();

      // Auto-stamp exhibit/solicitation statuses based on included documents
      if (reviewPackages.length > 0 && updateC) {
        const pkg = reviewPackages[0];
        const includedIds = (pkg.documents || []).map(d => d.id);
        const myProfile = profiles[currentUserId];
        const approverName = myProfile?.contact_name || 'Reviewer';
        const approvedAt = new Date().toISOString();
        const statusUpdates = {};
        if (includedIds.includes('exhibitA') || includedIds.includes('fullPackage')) {
          statusUpdates.exAStatus = status;
          if (status === 'approved') { statusUpdates.exAApprovedBy = approverName; statusUpdates.exAApprovedAt = approvedAt; }
        }
        if (includedIds.includes('exhibitB') || includedIds.includes('fullPackage')) {
          statusUpdates.exBStatus = status;
          if (status === 'approved') { statusUpdates.exBApprovedBy = approverName; statusUpdates.exBApprovedAt = approvedAt; }
        }
        if (includedIds.includes('exhibitD') || includedIds.includes('fullPackage')) {
          statusUpdates.exDStatus = status;
          if (status === 'approved') { statusUpdates.exDApprovedBy = approverName; statusUpdates.exDApprovedAt = approvedAt; }
        }
        if (includedIds.includes('solicitation') || includedIds.includes('fullPackage')) {
          statusUpdates.solStatus = status;
          if (status === 'approved') { statusUpdates.solApprovedBy = approverName; statusUpdates.solApprovedAt = approvedAt; }
        }
        if (Object.keys(statusUpdates).length > 0) {
          updateC({ pennyDrafts: statusUpdates });
        }
      }

      // If needs revision, notify owner with open comment count
      if (status === 'needs_revision' && !isOwner) {
        const ownerProfile = profiles[contract.ownerId];
        const myProfile = profiles[currentUserId];
        const openCount = rootComments.filter(c => !c.resolved).length;
        if (ownerProfile?.contact_email) {
          await sendEmailNotification(
            ownerProfile.contact_email,
            ownerProfile.contact_name || 'Analyst',
            'Revision requested on "' + contract.title + '"',
            (myProfile?.contact_name || 'A reviewer') + ' has requested revisions on "' + contract.title + '".\n\n' + openCount + ' open comment(s) require your attention.\n\nLog in to GoldenScope, open the Sharing tab, and review the comments left by your reviewer.'
          );
        }
      }
      toast.success(status === 'approved' ? '✅ Contract marked as Approved' : '⚠ Revision requested — analyst notified');
    }
  }

  // ── Penny Functions ──────────────────────────────────────────────────────────

  async function pennySummarizeComments() {
    if (!apiKey || !callAI || comments.length === 0) return;
    setPennyLoading('summary');
    try {
      const openComments = rootComments.filter(c => !c.resolved);
      const commentText = openComments.map((c, i) => {
        const author = profiles[c.user_id]?.contact_name || 'Reviewer';
        const section = SECTION_LABELS[c.section] || c.section || 'General';
        const replies = repliesMap[c.id] || [];
        let thread = `${i+1}. [${section}${c.field ? ' · ' + c.field : ''}] ${author}: "${c.comment}"`;
        if (replies.length > 0) thread += '\n   Replies: ' + replies.map(r => (profiles[r.user_id]?.contact_name || 'User') + ': "' + r.comment + '"').join(' | ');
        return thread;
      }).join('\n\n');
      const text = await callAI(apiKey, {
        system: 'You are Penny summarizing reviewer comments on a California state contract. Group comments by urgency: CRITICAL (blocking issues), IMPORTANT (should address), MINOR (suggestions). For each group list the key points concisely. End with a recommended action order for the analyst. Be specific and cite which sections are flagged.',
        messages: [{ role: 'user', content: 'Contract: "' + contract.title + '"\n\nOpen Comments (' + openComments.length + '):\n\n' + commentText + '\n\nPlease summarize and prioritize these comments.' }],
        max_tokens: 800,
      });
      setPennySummary(text);
    } catch (e) { toast.error('Penny summary failed: ' + e.message); }
    finally { setPennyLoading(''); }
  }

  async function pennyResolutionAssist(comment) {
    if (!apiKey || !callAI) return;
    setSelectedComment(comment);
    setPennyResolution('');
    setPennyLoading('resolution');
    try {
      const author = profiles[comment.user_id]?.contact_name || 'Reviewer';
      const section = SECTION_LABELS[comment.section] || comment.section || 'General';
      const s215 = contract.s215 || {};
      const context = `Contract: "${contract.title}" | Method: ${contract.procMethod || 'TBD'} | Budget: $${contract.budget || 'TBD'} | ${contract.isIT === 'yes' ? 'IT' : 'Non-IT'}`;
      const text = await callAI(apiKey, {
        system: 'You are Penny helping a California state contract analyst respond to a reviewer comment. Provide: 1) A brief explanation of why this concern is valid or not. 2) A specific recommended revision or action to address it. 3) A suggested written reply to the reviewer. Be concise and cite SCM sections where relevant.',
        messages: [{ role: 'user', content: `${context}

Reviewer (${author}) commented on [${section}${comment.field ? ' · ' + comment.field : ''}]:
"${comment.comment}"

How should the analyst address this?` }],
        max_tokens: 600,
      });
      setPennyResolution(text);
    } catch (e) { toast.error('Penny assist failed: ' + e.message); }
    finally { setPennyLoading(''); }
  }


  // Organize comments into threads
  const rootComments = comments.filter(c => !c.parent_id);
  const repliesMap = {};
  comments.filter(c => c.parent_id).forEach(r => {
    if (!repliesMap[r.parent_id]) repliesMap[r.parent_id] = [];
    repliesMap[r.parent_id].push(r);
  });

  const filteredComments = rootComments.filter(c => {
    if (!showResolved && c.resolved) return false;
    if (filterSection !== 'all' && c.section !== filterSection) return false;
    return true;
  });

  const unresolvedCount = rootComments.filter(c => !c.resolved).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid var(--slate-100)', paddingBottom: 0 }}>
        {[['comments', `💬 Comments${unresolvedCount > 0 ? ' (' + unresolvedCount + ')' : ''}`], ['review', '📋 Review History'], ['penny', '✦ Penny Review'], ['sharing', '👥 Access & Sharing']].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            style={{ padding: '8px 16px', fontSize: 13, fontWeight: activeTab === key ? 700 : 400, color: activeTab === key ? 'var(--navy-700)' : 'var(--slate-500)', background: 'none', border: 'none', borderBottom: '2px solid ' + (activeTab === key ? 'var(--navy-700)' : 'transparent'), cursor: 'pointer', marginBottom: -2 }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Comments Tab ── */}
      {activeTab === 'comments' && (
        <div>
          {/* Review status banner */}
          {contract.reviewStatus && (
            <div style={{ marginBottom: 12, padding: '10px 16px', borderRadius: 'var(--r-md)', background: contract.reviewStatus === 'approved' ? 'var(--green-50)' : 'var(--orange-50)', border: '1px solid ' + (contract.reviewStatus === 'approved' ? 'var(--green-300)' : 'var(--orange-300)'), display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 15 }}>{contract.reviewStatus === 'approved' ? '✅' : '⚠'}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: contract.reviewStatus === 'approved' ? 'var(--green-700)' : 'var(--orange-700)' }}>
                  {contract.reviewStatus === 'approved' ? 'Contract Approved' : 'Needs Revision'}
                </div>
                {contract.reviewedAt && <div style={{ fontSize: 11, color: 'var(--slate-500)' }}>{new Date(contract.reviewedAt).toLocaleDateString()}</div>}
              </div>
              {isOwner && <button className="btn btn-ghost btn-xs" style={{ marginLeft: 'auto' }} onClick={() => { supabase.from('contracts').update({ review_status: null }).eq('id', contract.id); if (updateC) updateC({ reviewStatus: null }); }}>Clear</button>}
            </div>
          )}

          {/* Approval actions */}
          {canApprove && contract.reviewStatus === 'pending' && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, padding: '12px 16px', background: 'var(--navy-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--navy-200)', alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--navy-700)' }}>Review Decision</div>
                <div style={{ fontSize: 11, color: 'var(--slate-500)', marginTop: 2 }}>
                  {rootComments.filter(c => !c.resolved).length} open comment(s) — resolve all before approving
                </div>
              </div>
              <button className="btn btn-sm" style={{ background: 'var(--green-600)', color: '#fff', border: 'none' }}
                onClick={() => { setPendingDecision('approved'); setDecisionNotes(''); setIsBypassing(false); setBypassReason(''); setShowApprovalModal(true); }}>✅ Approve</button>
              <button className="btn btn-sm" style={{ background: 'var(--orange-500)', color: '#fff', border: 'none' }}
                onClick={() => { setPendingDecision('needs_revision'); setDecisionNotes(''); setIsBypassing(false); setBypassReason(''); setShowApprovalModal(true); }}>⚠ Needs Revision</button>
            </div>
          )}

          {/* Approved — offer stage advance */}
          {contract.reviewStatus === 'approved' && isOwner && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, padding: '12px 16px', background: 'var(--green-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--green-200)', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--green-700)' }}>✅ Review Approved</div>
                <div style={{ fontSize: 11.5, color: 'var(--green-600)', marginTop: 2 }}>Ready to advance to the next stage</div>
              </div>
              {contract.stage < 14 && (
                <button className="btn btn-gold btn-sm" onClick={() => { if (onAdvanceStage) onAdvanceStage(contract.stage + 1); }}>
                  Advance to Stage {contract.stage + 2} →
                </button>
              )}
            </div>
          )}

          {/* Needs revision — highlight for analyst */}
          {contract.reviewStatus === 'needs_revision' && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, padding: '12px 16px', background: 'var(--orange-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--orange-300)', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--orange-700)' }}>⚠ Revision Requested</div>
                <div style={{ fontSize: 11.5, color: 'var(--orange-600)', marginTop: 2 }}>
                  {rootComments.filter(c => !c.resolved).length} open comment(s) to address — resolve them, then submit a new review package
                </div>
              </div>
              {isOwner && <button className="btn btn-ghost btn-xs" style={{ marginLeft: 'auto' }} onClick={() => { supabase.from('contracts').update({ review_status: null }).eq('id', contract.id); if (updateC) updateC({ reviewStatus: null }); }}>Clear</button>}
            </div>
          )}

          {/* New comment */}
          {canComment && (
            <div style={{ marginBottom: 20, padding: '14px 16px', background: 'var(--navy-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--navy-100)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-700)', marginBottom: 10 }}>Leave a Comment</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <select value={newSection} onChange={e => setNewSection(e.target.value)}
                  style={{ padding: '6px 10px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12.5, fontFamily: 'inherit', background: '#fff' }}>
                  {Object.entries(SECTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <input value={newField} onChange={e => setNewField(e.target.value)}
                  placeholder="Field (optional, e.g. budget, scope)"
                  style={{ flex: 1, padding: '6px 10px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12.5, fontFamily: 'inherit' }} />
              </div>
              <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
                placeholder="Write your comment or annotation here… (Shift+Enter for new line, Enter to submit)"
                rows={3}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                style={{ width: '100%', padding: '8px 12px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-gold btn-sm" disabled={submitting || !newComment.trim()} onClick={submitComment}>
                  {submitting ? 'Posting…' : '+ Post Comment'}
                </button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={filterSection} onChange={e => setFilterSection(e.target.value)}
              style={{ padding: '5px 10px', border: '1px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12, fontFamily: 'inherit' }}>
              <option value="all">All sections</option>
              {Object.entries(SECTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--slate-500)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showResolved} onChange={e => setShowResolved(e.target.checked)} />
              Show resolved
            </label>
            {unresolvedCount > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'var(--orange-100)', color: 'var(--orange-700)' }}>
                {unresolvedCount} open
              </span>
            )}
          </div>

          {/* Comment list */}
          {filteredComments.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', fontSize: 13, color: 'var(--slate-400)', fontStyle: 'italic' }}>
              {rootComments.length === 0 ? 'No comments yet. Be the first to leave a review note.' : 'No comments match your filter.'}
            </div>
          ) : (
            filteredComments.map(c => (
              <CommentThread
                key={c.id} comment={c}
                replies={repliesMap[c.id] || []}
                currentUserId={currentUserId}
                isOwner={isOwner}
                canComment={canComment}
                onReply={replyToComment}
                onResolve={resolveComment}
                onDelete={deleteComment}
                profiles={profiles}
              />
            ))
          )}
        </div>
      )}

      {/* ── Review History Tab ── */}
      {activeTab === 'review' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {(['legal', 'supervisor']).map(stage => {
            const stageInfo = REVIEW_STAGES[stage];
            const stagePkgs = reviewPackages.filter(p => p.review_stage === stage);
            const latestPkg = stagePkgs[0] || null;
            const latestStatus = latestPkg?.status || null;
            const showForm = showSubmitForm === stage;
            const statusColors = {
              pending:        { bg: 'var(--blue-50)',   border: 'var(--blue-200)',   color: 'var(--blue-700)',   label: '⏳ Pending Review' },
              approved:       { bg: 'var(--green-50)',  border: 'var(--green-200)',  color: 'var(--green-700)',  label: '✅ Approved' },
              needs_revision: { bg: 'var(--orange-50)', border: 'var(--orange-200)', color: 'var(--orange-700)', label: '⚠ Needs Revision' },
            };
            const stageVersionCount = stagePkgs.length;
            return (
              <div key={stage} className="card" style={{ borderLeft: '3px solid ' + stageInfo.color }}>
                {/* Card header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <h3 className="card-title" style={{ marginBottom: 2, color: stageInfo.color }}>{stageInfo.label}</h3>
                    <p style={{ fontSize: 12, color: 'var(--slate-500)', margin: 0 }}>
                      {stage === 'legal' ? 'Send contract package to legal counsel for review' : 'Send contract package to supervisor for final approval'}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {latestStatus && (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: statusColors[latestStatus]?.border, color: statusColors[latestStatus]?.color }}>
                        {statusColors[latestStatus]?.label}
                      </span>
                    )}
                    {isOwner && (
                      <button className="btn btn-gold btn-sm" onClick={() => { setShowSubmitForm(showForm ? null : stage); setReviewStage(stage); setSelectedDocs([]); setPackageNote(''); }}>
                        {showForm ? 'Cancel' : latestStatus === 'approved' ? '+ New Package' : '+ Submit'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Submit form */}
                {showForm && isOwner && (
                  <div style={{ borderTop: '1px solid var(--slate-100)', paddingTop: 14, marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate-600)', marginBottom: 8 }}>Select documents to include:</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                      {[
                        { id: 'std215', label: 'STD 215 — Approval/Routing Form', available: true },
                        { id: 'std213', label: 'STD 213 — Standard Agreement', available: true },
                        { id: 'exhibitA', label: 'Exhibit A — Scope of Work', available: !!(contract.pennyDrafts?.exAFinal || contract.pennyDrafts?.sow) },
                        { id: 'exhibitB', label: 'Exhibit B — Budget Detail', available: !!(contract.pennyDrafts?.exBFinal) },
                        { id: 'exhibitD', label: 'Exhibit D — Special Terms', available: !!(contract.pennyDrafts?.exDFinal) },
                        { id: 'solicitation', label: 'Solicitation Document', available: !!(contract.pennyDrafts?.sol) },
                        { id: 'fullPackage', label: 'Full Contract Package (Assembly)', available: !!(contract.exhibits?.length) },
                      ].map(doc => (
                        <label key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 'var(--r-md)', background: doc.available ? '#fff' : 'var(--slate-50)', border: '1px solid var(--slate-200)', cursor: doc.available ? 'pointer' : 'not-allowed', opacity: doc.available ? 1 : 0.5 }}>
                          <input type="checkbox" disabled={!doc.available}
                            checked={selectedDocs.some(d => d.id === doc.id)}
                            onChange={e => setSelectedDocs(prev => e.target.checked ? [...prev, { id: doc.id, label: doc.label }] : prev.filter(d => d.id !== doc.id))} />
                          <span style={{ fontSize: 13, color: 'var(--navy-700)' }}>{doc.label}</span>
                          {!doc.available && <span style={{ fontSize: 11, color: 'var(--slate-400)', marginLeft: 'auto' }}>Not yet available</span>}
                        </label>
                      ))}
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate-600)', marginBottom: 6 }}>Note to reviewers (optional):</div>
                      <textarea value={packageNote} onChange={e => setPackageNote(e.target.value)} rows={2}
                        placeholder={'e.g. ' + (stage === 'legal' ? 'Please review Exhibit A scope and confirm procurement method compliance…' : 'Ready for final approval — all legal comments have been addressed…')}
                        style={{ width: '100%', padding: '8px 12px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                    </div>
                    <button className="btn btn-gold" disabled={submittingPackage || selectedDocs.length === 0} onClick={submitReviewPackage}>
                      {submittingPackage ? 'Submitting…' : '📤 Submit for ' + stageInfo.label + ' (v' + (stageVersionCount + 1) + ')'}
                    </button>
                  </div>
                )}

                {/* Stage history */}
                {stagePkgs.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: 'var(--slate-400)', fontStyle: 'italic' }}>No packages submitted for {stageInfo.label.toLowerCase()} yet.</div>
                ) : stagePkgs.map((pkg, i) => {
                  const submitter = profiles[pkg.submitted_by]?.contact_name || 'Analyst';
                  const decider = pkg.decided_by ? profiles[pkg.decided_by]?.contact_name : null;
                  const sc = statusColors[pkg.status] || statusColors.pending;
                  return (
                    <div key={pkg.id} style={{ marginBottom: i < stagePkgs.length - 1 ? 8 : 0, padding: '12px 14px', borderRadius: 'var(--r-md)', border: '1px solid ' + sc.border, background: sc.bg }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-800)' }}>v{pkg.version}</span>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: sc.border, color: sc.color }}>{sc.label}</span>
                            {pkg.bypassed && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: 'var(--orange-100)', color: 'var(--orange-600)', border: '1px solid var(--orange-300)' }}>⚡ Bypassed</span>}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--slate-500)', marginTop: 3 }}>
                            Submitted by {submitter} · {new Date(pkg.submitted_at).toLocaleDateString()}
                          </div>
                          {decider && pkg.decided_at && (
                            <div style={{ fontSize: 11, color: sc.color, marginTop: 2 }}>
                              {pkg.status === 'approved' ? 'Approved' : 'Revision requested'} by {decider} · {new Date(pkg.decided_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        {pkg.open_comments > 0 && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, background: 'var(--orange-100)', color: 'var(--orange-700)', fontWeight: 600 }}>{pkg.open_comments} comment(s)</span>}
                      </div>
                      {pkg.note && <div style={{ fontSize: 12, color: 'var(--slate-600)', marginTop: 6, fontStyle: 'italic' }}>Analyst: "{pkg.note}"</div>}
                      {pkg.decision_notes && <div style={{ fontSize: 12, color: sc.color, marginTop: 4, fontStyle: 'italic' }}>Reviewer: "{pkg.decision_notes}"</div>}
                      {pkg.bypassed && pkg.bypass_reason && <div style={{ fontSize: 11.5, color: 'var(--orange-600)', marginTop: 4, fontWeight: 600 }}>⚡ Bypass: {pkg.bypass_reason}</div>}
                      {pkg.documents?.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          {pkg.documents.map((d, j) => <span key={j} style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: '#fff', border: '1px solid var(--slate-200)', color: 'var(--navy-600)' }}>{d.label}</span>)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Penny Tab ── */}
      {activeTab === 'penny' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* 1. Comment Summarizer */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div>
                <h3 className="card-title" style={{ marginBottom: 2 }}>✦ Summarize Open Comments</h3>
                <p style={{ fontSize: 12, color: 'var(--slate-500)', margin: 0 }}>Penny reads all open comments and groups them by urgency with a recommended action order.</p>
              </div>
              <button className="btn btn-gold btn-sm" disabled={pennyLoading === 'summary' || comments.filter(c => !c.resolved && !c.parent_id).length === 0}
                onClick={pennySummarizeComments}>
                {pennyLoading === 'summary' ? <><div className="spin spin-sm" /> Summarizing…</> : pennySummary ? '↺ Re-summarize' : '✦ Summarize'}
              </button>
            </div>
            {pennyLoading === 'summary' && <div style={{ fontSize: 12, color: 'var(--gold-600)', fontStyle: 'italic' }}>Penny is reading all comments…</div>}
            {comments.filter(c => !c.resolved && !c.parent_id).length === 0 && !pennySummary && (
              <div style={{ fontSize: 12, color: 'var(--slate-400)', fontStyle: 'italic' }}>No open comments to summarize yet.</div>
            )}
            {pennySummary && !pennyLoading && (
              <div style={{ padding: '14px 16px', background: 'var(--navy-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--navy-100)', fontSize: 12.5, lineHeight: 1.8, color: 'var(--navy-800)', whiteSpace: 'pre-wrap' }}>
                {pennySummary}
              </div>
            )}
          </div>

          {/* 2. Resolution Assistant */}
          <div className="card">
            <div style={{ marginBottom: 12 }}>
              <h3 className="card-title" style={{ marginBottom: 2 }}>✦ Resolution Assistant</h3>
              <p style={{ fontSize: 12, color: 'var(--slate-500)', margin: 0 }}>Select any open comment and Penny will explain how to address it and draft a reply.</p>
            </div>
            {rootComments.filter(c => !c.resolved).length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--slate-400)', fontStyle: 'italic' }}>No open comments to resolve.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                {rootComments.filter(c => !c.resolved).map(c => {
                  const author = profiles[c.user_id]?.contact_name || 'Reviewer';
                  const section = SECTION_LABELS[c.section] || c.section || 'General';
                  const isSelected = selectedComment?.id === c.id;
                  return (
                    <div key={c.id} onClick={() => pennyResolutionAssist(c)}
                      style={{ padding: '10px 14px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (isSelected ? 'var(--gold-400)' : 'var(--slate-200)'), background: isSelected ? 'var(--gold-50)' : '#fff', cursor: 'pointer', transition: 'all .15s' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-500)', marginBottom: 3 }}>{author} · {section}{c.field ? ' · ' + c.field : ''}</div>
                      <div style={{ fontSize: 12.5, color: 'var(--navy-700)' }}>{c.comment.length > 100 ? c.comment.slice(0, 100) + '…' : c.comment}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {pennyLoading === 'resolution' && <div style={{ fontSize: 12, color: 'var(--gold-600)', fontStyle: 'italic', marginTop: 8 }}>Penny is working on a resolution…</div>}
            {pennyResolution && !pennyLoading && selectedComment && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-500)', marginBottom: 6 }}>Penny's guidance for: "{selectedComment.comment.slice(0, 60)}…"</div>
                <div style={{ padding: '14px 16px', background: 'var(--navy-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--navy-100)', fontSize: 12.5, lineHeight: 1.8, color: 'var(--navy-800)', whiteSpace: 'pre-wrap' }}>
                  {pennyResolution}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => { navigator.clipboard.writeText(pennyResolution); toast.success('Copied'); }}>📋 Copy</button>
                  <button className="btn btn-ghost btn-xs" style={{ color: 'var(--slate-400)' }} onClick={() => { setPennyResolution(''); setSelectedComment(null); }}>Clear</button>
                </div>
              </div>
            )}
          </div>

        </div>
      )}

      {/* ── Sharing Tab ── */}
      {activeTab === 'sharing' && (
        <div className="card">
          <h3 className="card-title">Access & Sharing</h3>
          <p style={{ fontSize: 12.5, color: 'var(--slate-500)', marginBottom: 16 }}>
            Share this contract with colleagues. Reviewers and Approvers can leave comments. Editors can also edit fields and advance stages.
          </p>

          {/* Access list */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-400)', marginBottom: 8 }}>Who Has Access</div>

            {/* Owner */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--navy-50)', border: '1px solid var(--navy-100)', borderRadius: 'var(--r-md)', marginBottom: 6 }}>
              <Avatar name={contract?.analystName || 'O'} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{contract?.analystName || 'Contract Owner'} {isOwner ? '(you)' : ''}</div>
                <div style={{ fontSize: 11, color: 'var(--slate-500)' }}>Owner — full access</div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'var(--navy-200)', color: 'var(--navy-800)' }}>Owner</span>
            </div>

            {loading ? (
              <div style={{ fontSize: 12, color: 'var(--slate-400)', padding: '8px 0' }}>Loading…</div>
            ) : members.length === 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--slate-400)', fontStyle: 'italic', padding: '6px 0' }}>No other members yet.</div>
            ) : members.map(m => {
              const name = profiles[m.user_id]?.contact_name || 'Unknown';
              const email = profiles[m.user_id]?.contact_email || '';
              const role = m.role || 'reviewer';
              const roleInfo = ROLE_CONFIG[role] || ROLE_CONFIG.reviewer;
              return (
                <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fff', border: '1px solid var(--slate-200)', borderRadius: 'var(--r-md)', marginBottom: 6 }}>
                  <Avatar name={name} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{name}</div>
                    <div style={{ fontSize: 11, color: 'var(--slate-400)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</div>
                  </div>
                  {isOwner ? (
                    <select value={role} onChange={e => updateRole(m.user_id, e.target.value)}
                      style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--slate-200)', fontSize: 12, color: roleInfo.color, fontWeight: 600, background: roleInfo.bg }}>
                      {Object.entries(ROLE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, color: roleInfo.color, background: roleInfo.bg, border: '1px solid currentColor' }}>{roleInfo.label}</span>
                  )}
                  {isOwner && (
                    <button onClick={() => removeMember(m.user_id, name)} disabled={removing === m.user_id}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red-400)', fontSize: 18, padding: '0 4px', lineHeight: 1 }}>
                      {removing === m.user_id ? '…' : '×'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Invite */}
          {isOwner && (
            <div style={{ borderTop: '1px solid var(--slate-100)', paddingTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-400)', marginBottom: 10 }}>Invite Someone</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input type="email" placeholder="Enter their GoldenScope email…" value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && invite()}
                  style={{ flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }} />
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
                  style={{ padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}>
                  {Object.entries(ROLE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <button className="btn btn-gold" disabled={inviting || !inviteEmail.trim()} onClick={invite}>
                  {inviting ? 'Adding…' : '+ Invite'}
                </button>
              </div>
              <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {Object.entries(ROLE_CONFIG).map(([k, v]) => (
                  <div key={k} style={{ fontSize: 11.5, padding: '8px 10px', borderRadius: 'var(--r-md)', background: v.bg, border: '1px solid ' + v.color + '33' }}>
                    <span style={{ fontWeight: 700, color: v.color }}>{v.label}:</span> <span style={{ color: 'var(--slate-600)' }}>{v.desc}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--slate-400)', fontStyle: 'italic' }}>
                Person must have a GoldenScope account at your agency. An email notification will be sent automatically.
              </div>
            </div>
          )}

          {!isOwner && (
            <div style={{ fontSize: 12, color: 'var(--slate-400)', fontStyle: 'italic', borderTop: '1px solid var(--slate-100)', paddingTop: 10 }}>
              Only the contract owner can manage sharing.
            </div>
          )}
        </div>
      )}

      {/* ── Approval Decision Modal ── */}
      {/* ── Approval Decision Modal ── */}
      {showApprovalModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 'var(--r-xl)', padding: '28px 32px', maxWidth: 480, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,.2)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy-800)', marginBottom: 6 }}>
              {pendingDecision === 'approved' ? '✅ Confirm Approval' : '⚠ Request Revisions'}
            </h3>
            <p style={{ fontSize: 12.5, color: 'var(--slate-500)', marginBottom: 18 }}>
              {pendingDecision === 'approved'
                ? 'Approve this contract package. You can add notes for the analyst or record a bypass justification.'
                : 'Request revisions from the analyst. Comments below will explain what needs to be addressed.'}
            </p>

            {/* Bypass option — only on approval */}
            {pendingDecision === 'approved' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', borderRadius: 'var(--r-md)', background: isBypassing ? 'var(--orange-50)' : 'var(--slate-50)', border: '1px solid ' + (isBypassing ? 'var(--orange-300)' : 'var(--slate-200)'), cursor: 'pointer' }}>
                <input type="checkbox" checked={isBypassing} onChange={e => setIsBypassing(e.target.checked)} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: isBypassing ? 'var(--orange-700)' : 'var(--slate-700)' }}>⚡ Bypassing reviewer</div>
                  <div style={{ fontSize: 11.5, color: 'var(--slate-500)' }}>Check this if approving on behalf of or in lieu of the assigned reviewer</div>
                </div>
              </label>
            )}

            {/* Bypass reason — required if bypassing */}
            {isBypassing && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--orange-700)', marginBottom: 6 }}>Bypass Reason (required for audit trail):</div>
                <textarea value={bypassReason} onChange={e => setBypassReason(e.target.value)} rows={2}
                  placeholder="e.g. Legal review waived per division policy — contract under $25K threshold…"
                  style={{ width: '100%', padding: '8px 12px', border: '1.5px solid var(--orange-300)', borderRadius: 'var(--r-md)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            )}

            {/* Decision notes */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate-600)', marginBottom: 6 }}>
                {pendingDecision === 'approved' ? 'Notes for analyst (optional):' : 'Notes for analyst (recommended):'}
              </div>
              <textarea value={decisionNotes} onChange={e => setDecisionNotes(e.target.value)} rows={3}
                placeholder={pendingDecision === 'approved'
                  ? 'e.g. Approved with the understanding that Exhibit A will be updated before execution…'
                  : 'e.g. Please revise the scope in Exhibit A — too broad, needs specific deliverables and timeline…'}
                style={{ width: '100%', padding: '8px 12px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => { setShowApprovalModal(false); setPendingDecision(null); }}>Cancel</button>
              <button className="btn btn-sm" disabled={isBypassing && !bypassReason.trim()}
                style={{ background: pendingDecision === 'approved' ? 'var(--green-600)' : 'var(--orange-500)', color: '#fff', border: 'none', opacity: isBypassing && !bypassReason.trim() ? 0.5 : 1 }}
                onClick={confirmApprovalDecision}>
                {pendingDecision === 'approved' ? '✅ Confirm Approval' : '⚠ Confirm — Request Revisions'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}